import React, { useState, useContext } from 'react';
import api from '../utilities/api';
import AuthContext from '../contexts/AuthContext';
import ErrorMessage from './common/ErrorMessage';

const AccountSettingsModal = ({ onClose }) => {
    const { user, updateUser } = useContext(AuthContext);
    const [activeTab, setActiveTab] = useState('profile');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Profile state
    const [displayName, setDisplayName] = useState(user?.forename || '');
    const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

    // Password state
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const clearMessages = () => {
        setError(null);
        setSuccess(null);
    };

    const handleTabChange = (tab) => {
        clearMessages();
        setActiveTab(tab);
    };

    const handleUpdateDisplayName = async (e) => {
        e.preventDefault();
        clearMessages();

        const trimmedName = displayName.trim();
        if (!trimmedName) {
            setError('Display name cannot be empty');
            return;
        }
        if (trimmedName.length > 10) {
            setError('Display name must be 10 characters or less');
            return;
        }
        if (trimmedName === user?.forename) {
            setSuccess('No changes to save');
            return;
        }

        setIsUpdatingProfile(true);
        try {
            const response = await api.put('/user/profile', { forename: trimmedName });
            if (response.data.success) {
                updateUser({ forename: response.data.user.forename });
                setSuccess('Display name updated successfully');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update display name');
        } finally {
            setIsUpdatingProfile(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        clearMessages();

        if (!oldPassword || !newPassword || !confirmPassword) {
            setError('All fields are required');
            return;
        }
        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        setIsChangingPassword(true);
        try {
            const response = await api.post('/user/change-password', {
                old_password: oldPassword,
                new_password: newPassword
            });
            if (response.data.success) {
                setSuccess('Password changed successfully');
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to change password');
        } finally {
            setIsChangingPassword(false);
        }
    };

    return (
        <div className="scoreModal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Account settings</h2>
                <button onClick={onClose} className="modal-close-btn">×</button>
            </div>

            <ErrorMessage message={error} onDismiss={() => setError(null)} />

            {success && (
                <div className="success-message">
                    <span className="success-message-text">{success}</span>
                    <button
                        onClick={() => setSuccess(null)}
                        className="success-message-dismiss"
                        type="button"
                        aria-label="Dismiss message"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="modal-tabs">
                <button
                    onClick={() => handleTabChange('profile')}
                    className={`modal-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
                >
                    Profile
                </button>
                <button
                    onClick={() => handleTabChange('security')}
                    className={`modal-tab-btn ${activeTab === 'security' ? 'active' : ''}`}
                >
                    Security
                </button>
            </div>

            {activeTab === 'profile' && (
                <div>
                    <div className="formGroup">
                        <label>Username</label>
                        <input
                            type="text"
                            value={user?.username || ''}
                            disabled
                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                        />
                    </div>

                    <form onSubmit={handleUpdateDisplayName}>
                        <div className="formGroup">
                            <label>Display name</label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                maxLength={10}
                                placeholder="Your display name"
                            />
                        </div>

                        <div className="modal-button-row">
                            <button
                                type="submit"
                                disabled={isUpdatingProfile}
                            >
                                {isUpdatingProfile ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {activeTab === 'security' && (
                <form onSubmit={handleChangePassword}>
                    <div className="formGroup">
                        <label>Current password</label>
                        <input
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            placeholder="Enter current password"
                        />
                    </div>

                    <div className="formGroup">
                        <label>New password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="At least 8 characters"
                        />
                    </div>

                    <div className="formGroup">
                        <label>Confirm new password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                        />
                    </div>

                    <div className="modal-button-row">
                        <button
                            type="submit"
                            disabled={isChangingPassword}
                        >
                            {isChangingPassword ? 'Changing...' : 'Change Password'}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default AccountSettingsModal;
